import React from "react";
// UI Framework
import { toast } from "react-toastify";
import { TableCell, TableRow } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
// Common Components
import { DatePicker, TextInput, Loading, Table, Button } from "src/Components";
// Services
import {
  addCash,
  getClashFlowTable,
  deleteCashFlowRecord,
} from "src/Requests/imePortfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { addComma, removeComma } from "src/helper";
// Types
import {
  IUserPositionSummarySearchParams,
  CashFlowSearchParams,
  CashFlowTableReport,
} from "src/Interfaces/imePortfolio";

interface IProps {
  searchParams: IUserPositionSummarySearchParams;
  setRefresh: (flag: boolean) => void;
}

const CashFlowTable = ({ searchParams, setRefresh }: IProps) => {
  const [loading, setLoading] = React.useState(false);
  const [baseConfig, setBaseConfig] = React.useState<CashFlowTableReport[]>([]);
  const [searchParam, setSearchParam] = React.useState({
    userId: searchParams.userId,
  } as CashFlowSearchParams);

  const fetchCashFlowTable = React.useCallback(() => {
    setLoading(true);
    getClashFlowTable(searchParams.userId)
      .then((resp) => {
        setBaseConfig(resp);
        setSearchParam({} as CashFlowSearchParams);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [searchParams.userId]);

  React.useEffect(() => {
    setSearchParam((prev) => ({
      ...prev,
      userId: searchParams.userId,
    }));
    fetchCashFlowTable();
  }, [fetchCashFlowTable, searchParams.userId]);

  const changeHandler = (name: string, value: any) => {
    setSearchParam((prev) => ({
      ...prev,
      [name]: removeComma(value),
      userId: searchParams.userId,
    }));
  };

  return (
    <div className="w-full h-full px-4">
      <div className="flex justify-between items-end mb-3">
        <span>جدول واریز</span>
      </div>
      <div>
        {loading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Loading className="text-blue-500" />
          </div>
        ) : (
          <div className="border-2 border-blue-100 w-full overflow-visible">
            <Table
              header={<div />}
              body={
                <>
                  <TableRow>
                    <TableCell>
                      <div className="text-sm font-bold">تاریخ</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-bold">مبلغ</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-bold">عملیات</div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ zIndex: "30 !important" }}>
                      <DatePicker
                        label="تاریخ"
                        name="date"
                        onChange={changeHandler}
                        value={searchParam.date}
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        label="مبلغ"
                        name="amount"
                        onChange={changeHandler}
                        value={addComma(searchParam?.amount)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <div
                        className={`mx-10 flex items-center justify-center  transition-all w-5 h-5 rounded-full   ${searchParams.userId ? "hover:bg-green-600 bg-green-700 cursor-pointer" : "bg-gray-500 cursor-not-allowed"}`}
                        onClick={() => {
                          addCash({
                            ...searchParam,
                            userId: searchParams.userId,
                          }).then(() => {
                            fetchCashFlowTable();
                            setRefresh(true);
                          });
                        }}
                      >
                        <AddIcon
                          fontSize="small"
                          className={`text-white transition-all`}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  {baseConfig?.map((item) => (
                    <TableRow>
                      <TableCell>
                        <div className="w-full text-center">
                          {item.datePersian}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="w-full text-center">
                          {addComma(item.amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className="hover:bg-red-100 transition-all rounded p-1"
                          onClick={() => {
                            deleteCashFlowRecord(item.id).then(() => {
                              fetchCashFlowTable();
                              setRefresh(true);
                            });
                          }}
                        >
                          <DeleteIcon
                            fontSize="small"
                            className={`cursor-pointer text-red-600 transition-all`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CashFlowTable;
